import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { TextField } from "@components/Input/Input.styles";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import { rejectionSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/rejectionSchema";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.port-services";
const REJECTION_ATTACHMENT_UPLOAD_KEY = "portServicesRejectionAttachment";

const RejectionReasonAndAttachmentsComponent = () => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { setRejectionReason, rejectionReason, errors, setErrors, setRejectionAttachment } =
    usePortServicesFlow();

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const TEXTAREA_MAX_CHARS = 1000;
  const rejectionUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const rejectionFiles = useUploadStore(
    (s) => s.filesByKey[REJECTION_ATTACHMENT_UPLOAD_KEY] ?? EMPTY_UPLOAD_ENTRIES,
  );

  const fileProfiles = useFileUploadProfiles();
  const rejectionProfile = fileProfiles.rejectPortServiceDrawer;
  const allowedRejectionExtensions = rejectionProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setPreApproveRejectionAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(REJECTION_ATTACHMENT_UPLOAD_KEY);
        return;
      }
      setFilesForKey(REJECTION_ATTACHMENT_UPLOAD_KEY, files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeRejectionFile = () => {
    clearFilesForKey(REJECTION_ATTACHMENT_UPLOAD_KEY);
    setRejectionAttachment({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          setFilesForKey(REJECTION_ATTACHMENT_UPLOAD_KEY, [
            {
              name: file.name,
              url: documentUrl,
              fileType: file.type,
            },
          ]);
          setRejectionAttachment({
            documentUrl: documentUrl,
            documentName: file?.name,
            fileType: file?.type,
          });
          return { documentUrl, documentType };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | { externalFile?: string; rejectionFile?: string }
        | ((prev: { externalFile?: string; rejectionFile?: string }) => {
            externalFile?: string;
            rejectionFile?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ externalFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      rejectionUploadSignatureRef.current = null;
      removeRejectionFile();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2" data-testid="port-services-rejection-reason">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.rejection-reason`)}
      </Typography>
      <TextField
        multiline
        minRows={4}
        value={rejectionReason}
        inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
        placeholder={t(`${LANG_BASE}.fields.rejection-reason`)}
        sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!isPrintableKey(e)) return;
          if (rejectionReason.length >= TEXTAREA_MAX_CHARS) {
            setErrors({
              ...errors,
              rejectionReason: t("form-validation.max-length", {
                label: t(`${LANG_BASE}.fields.rejection-reason`),
                max: TEXTAREA_MAX_CHARS,
              }),
            });
          }
        }}
        onBlur={() => {
          const res = rejectionSchema().safeParse({ rejectionReason });
          if (!res.success) {
            const flat = res.error.flatten().fieldErrors;
            setErrors({ ...errors, rejectionReason: flat.rejectionReason?.[0] });
          } else {
            setErrors({ ...errors, rejectionReason: undefined });
          }
        }}
        onChange={(e) => {
          const val = e.target.value;
          setRejectionReason(val);
          const res = rejectionSchema().safeParse({ rejectionReason: val });
          if (!res.success) {
            const flat = res.error.flatten().fieldErrors;
            setErrors({ ...errors, rejectionReason: flat.rejectionReason?.[0] });
          } else {
            setErrors({ ...errors, rejectionReason: undefined });
          }
        }}
      />
      {errors.rejectionReason && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.rejectionReason}</FormHelperText>
      )}
      <FormHelperText sx={charCounterSx}>
        {rejectionReason.length}/{TEXTAREA_MAX_CHARS}
      </FormHelperText>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.rejection-attachments`)}
      </Typography>
      <DocumentUpload
        testId="port-services-rejection-attachment-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
              }),
            setUploadFiles: setPreApproveRejectionAttachment,
            onUploadDocument: (file) => handleDocumentUpload(file, "REJECTION_NOTES"),
            uploadTracker: rejectionUploadSignatureRef,
          })
        }
        name={REJECTION_ATTACHMENT_UPLOAD_KEY}
        conditions={rejectionProfile}
        files={rejectionFiles}
        setFiles={(next) => setFilesForKey(REJECTION_ATTACHMENT_UPLOAD_KEY, next)}
        maxFiles={1}
        allowedExtensions={allowedRejectionExtensions}
        uploadDescription={t("upload.additional-documents-upload", {
          size: rejectionProfile.maxSizeLabel,
          extensions: rejectionProfile.format.join(", "),
        })}
      />
      {errors.rejectionFile && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.rejectionFile}</FormHelperText>
      )}
      {rejectionFiles[0] && (
        <SingleFile
          file={rejectionFiles[0]}
          testId="port-services-rejection-attachment"
          fieldName={REJECTION_ATTACHMENT_UPLOAD_KEY}
          currentFileIndex={0}
          setFiles={() => {
            setErrors({ ...errors, rejectionFile: undefined });
            setPreApproveRejectionAttachment([]);
            rejectionUploadSignatureRef.current = null;
          }}
          removeFn={removeRejectionFile}
        />
      )}
    </div>
  );
};

export default RejectionReasonAndAttachmentsComponent;
